import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { FiUserPlus as UserPlus, FiHome as Building2, FiEdit2 as Edit } from 'react-icons/fi';
import { Card, Button } from '../../../components/ui';
import { apiCall } from '../../../services/realApi';

interface ClientRecord {
  id: string;
  name: string;
  email?: string;
  subscription_plan?: string;
  subscription_status?: string;
  trial_ends_at?: string;
  subscription_started_at?: string;
  created_at?: string;
  createdAt?: string;
}

interface EditingClient {
  id: string;
  subscription_plan: string;
  subscription_status: string;
  trial_ends_at: string;
  subscription_started_at: string;
}

interface NewClientForm {
  name: string;
  email: string;
  contactPerson: string;
}

const Clients = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState<EditingClient | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [newClient, setNewClient] = useState<NewClientForm>({
    name: '',
    email: '',
    contactPerson: ''
  });

  const loadClients = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try using the realApi first
      const data = await apiCall('/clients');
      setClients(data || []);
    } catch (err: unknown) {
      // Fallback to old API
      try {
        const { getClients } = await import('../../../services/api');
        const result = await getClients();
        if (result.success) {
          setClients(result.data || []);
        } else {
          const apiErr = err as { message?: string };
          setError(apiErr.message || 'Failed to load clients');
          setClients([]);
        }
      } catch {
        const apiErr = err as { message?: string };
        console.error('Error loading clients:', err);
        setError(apiErr.message || 'Failed to load clients');
        setClients([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadClients();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleCreateClient = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      // Build payload - only include fields with values
      const payload: Record<string, string> = {
        name: newClient.name
      };

      if (newClient.email?.trim()) {
        payload.email = newClient.email.trim();
      }

      if (newClient.contactPerson?.trim()) {
        payload.contactPerson = newClient.contactPerson.trim();
      }

      // Try using the realApi first
      const result = await apiCall('/clients', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (result) {
        setShowCreateModal(false);
        setNewClient({ name: '', email: '', contactPerson: '' });
        loadClients();
      }
    } catch (err: unknown) {
      // Fallback to old API
      try {
        const { createClient } = await import('../../../services/api');
        const result = await createClient(newClient);
        if (result.success) {
          setShowCreateModal(false);
          setNewClient({ name: '', email: '', contactPerson: '' });
          loadClients();
        } else {
          alert('Failed to create client: ' + result.error);
        }
      } catch {
        const apiErr = err as { message?: string };
        console.error('Error creating client:', err);
        alert('Failed to create client: ' + apiErr.message);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const handleViewProjects = (clientId: string) => {
    // Navigate to projects filtered by client
    navigate(`/hub/projects?client=${clientId}`);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatSubscriptionPlan = (plan?: string): string => {
    if (!plan) return 'N/A';
    return plan.split('_').map((word) =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const handleEditClient = (client: ClientRecord) => {
    setEditingClient({
      id: client.id,
      subscription_plan: client.subscription_plan || 'monthly',
      subscription_status: client.subscription_status || 'active',
      trial_ends_at: client.trial_ends_at?.split('T')[0] ?? '',
      subscription_started_at: client.subscription_started_at?.split('T')[0] ?? ''
    });
    setShowEditModal(true);
  };

  const handleUpdateClient = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingClient) return;
    setEditLoading(true);

    try {
      // Build payload with subscription updates
      const payload: Record<string, string> = {
        subscription_plan: editingClient.subscription_plan,
        subscription_status: editingClient.subscription_status
      };

      // Only include trial_ends_at if it's a trial plan and has a value
      if (editingClient.subscription_plan === 'trial' && editingClient.trial_ends_at) {
        payload.trial_ends_at = new Date(editingClient.trial_ends_at).toISOString();
      }

      // Include subscription_started_at for monthly/yearly plans
      if ((['monthly', 'yearly'].includes(editingClient.subscription_plan)) && editingClient.subscription_started_at) {
        payload.subscription_started_at = new Date(editingClient.subscription_started_at).toISOString();
      }

      await apiCall(`/clients/${editingClient.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      setShowEditModal(false);
      setEditingClient(null);
      loadClients();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      console.error('Error updating client:', err);
      alert('Failed to update client: ' + apiErr.message);
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">
            Client Management
          </h1>
          <p className="text-sm text-gray-400">
            Manage your client relationships and projects
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Card className="mb-6">
          <div className="p-6 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button
              variant="secondary"
              className="mt-4"
              onClick={loadClients}
            >
              Retry
            </Button>
          </div>
        </Card>
      )}

      {/* Active Clients Card */}
      <Card className="mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Active Clients
          </h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {clients.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                No clients found. Create your first client to get started.
              </p>
            </div>
          ) : (
            clients.map((client) => (
              <div key={client.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                      {client.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {client.email}
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Subscription Plan</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatSubscriptionPlan(client.subscription_plan)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          client.subscription_status === 'active'
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                            : client.subscription_status === 'expired'
                            ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}>
                          {client.subscription_status
                            ? client.subscription_status.charAt(0).toUpperCase() + client.subscription_status.slice(1)
                            : 'Active'}
                        </span>
                      </div>
                      {client.subscription_plan === 'trial' && client.trial_ends_at && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Trial Ends</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDate(client.trial_ends_at)}
                          </p>
                        </div>
                      )}
                      {(['monthly', 'yearly'].includes(client.subscription_plan ?? "")) && client.subscription_started_at && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Subscription Started</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDate(client.subscription_started_at)}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(client.created_at || client.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEditClient(client)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => handleViewProjects(client.id)}
                    >
                      View Projects
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Create Client Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <Card className="w-full max-w-md m-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Add New Client
              </h3>
              <form onSubmit={handleCreateClient}>
                <div className="mb-4">
                  <label htmlFor="hub-client-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Client Name
                  </label>
                  <input
                                                                                  id="hub-client-name"
                    type="text"
                    required
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter client name"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="hub-contact-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Email <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  <input
                                                                                  id="hub-contact-email"
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter contact email"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="hub-contact-person" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Person <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  <input
                                                                                  id="hub-contact-person"
                    type="text"
                    value={newClient.contactPerson}
                    onChange={(e) => setNewClient({ ...newClient, contactPerson: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter contact person name"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={createLoading}
                  >
                    {createLoading ? 'Adding...' : 'Add Client'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editingClient && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <Card className="w-full max-w-lg m-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Edit Client Subscription
              </h3>
              <form onSubmit={handleUpdateClient}>
                <div className="mb-4">
                  <label htmlFor="hub-subscription-plan" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subscription Plan
                  </label>
                  <select
                                                                                  id="hub-subscription-plan"
                    value={editingClient.subscription_plan}
                    onChange={(e) => setEditingClient({ ...editingClient, subscription_plan: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="assessment_only">Assessment Only</option>
                    <option value="trial">Trial</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="professional_services">Professional Services</option>
                    <option value="managed_services">Managed Services</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="hub-subscription-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subscription Status
                  </label>
                  <select
                                                                                  id="hub-subscription-status"
                    value={editingClient.subscription_status}
                    onChange={(e) => setEditingClient({ ...editingClient, subscription_status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {editingClient.subscription_plan === 'trial' && (
                  <div className="mb-4">
                    <label htmlFor="hub-trial-end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Trial End Date
                    </label>
                    <input
                                                                                  id="hub-trial-end-date"
                      type="date"
                      value={editingClient.trial_ends_at}
                      onChange={(e) => setEditingClient({ ...editingClient, trial_ends_at: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Set when the trial period should end
                    </p>
                  </div>
                )}

                {(['monthly', 'yearly'].includes(editingClient.subscription_plan)) && (
                  <div className="mb-4">
                    <label htmlFor="hub-subscription-start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subscription Start Date
                    </label>
                    <input
                                                                                  id="hub-subscription-start-date"
                      type="date"
                      value={editingClient.subscription_started_at}
                      onChange={(e) => setEditingClient({ ...editingClient, subscription_started_at: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Start date for calculating renewal (monthly: +1 month, yearly: +1 year)
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingClient(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={editLoading}
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Clients;
